import { Card, Typography, Row, Col, Button } from 'antd';
import { isNil } from 'lodash/fp';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { type WebPageButtonDataFragment } from '../../../../api/fragments/WebPageButtonData';
import Image from '../../../../components/Image';
import { useThemeComponents } from '../../../../themes';
import breakpoints from '../../../../utilities/breakpoints';
import useTranslatedString from '../../../../utilities/useTranslatedString';

const { Meta } = Card;

const { Title, Text } = Typography;
export const StyledCard = styled(Card)`
    height: 100%;
    color: var(--ant-primary-color);

    & .ant-card-cover {
        height: 280px;
        margin: 0;
        border-radius: inherit;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        & > .ant-image {
            height: 100%;
            border-radius: inherit;

            & > img {
                border-radius: inherit;
            }
        }
    }

    & .ant-card-body {
        padding: 24px 16px;
    }

    @media screen and (max-width: ${breakpoints.md}) {
        & .ant-card-cover {
            margin: 0;
        }

        & .ant-card-body {
            padding: 24px;
        }
    }
`;

export const StyledImage = styled(Image)`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const BannerImageCss = css`
    max-width: 100vw;
    width: 100%;
    height: auto;
    object-fit: cover;
`;

export const StyledBannerImage = styled(Image)`
    ${BannerImageCss}
`;

export const Video = styled.video`
    ${BannerImageCss}
`;

export const StyledImageBlockContainer = styled(Row)`
    max-width: var(--mobility-page-max-width);
    margin-left: auto;
    margin-right: auto;
`;

export const StyledCustomBlockContainer = styled(Row)`
    max-width: var(--mobility-page-max-width);
    margin-left: auto;
    margin-right: auto;
    padding: 50px;
`;

export const StyledImageBlockImageContainer = styled(Col)`
    & .ant-image {
        width: 100%;
        height: 100%;
    }
`;
export const StyledImageBlockImageDetailsContainer = styled(Col)`
    padding: 50px;
    display: flex;
    align-items: center;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 24px;
    }
`;

export const StyledImageBlockImageDetails = styled(Row)``;

export const StyledNavigationLink = styled.a<{ isSocialMedia: boolean }>`
    ${props =>
        !props.isSocialMedia &&
        css`
            margin-left: 10px;
        `}
`;

export const StyledTextImageDetailsContainer = styled.div<{ isBlackBackground: boolean }>`
    height: 100%;
    display: flex;
    ${props =>
        props.isBlackBackground &&
        css`
            background-color: #000000;
            color: #ffffff;

            .ant-typography {
                color: #ffffff;
            }

            & > .ant-typography {
                color: #ffffff;
                margin-top: 20px;
            }
            & > ul,
            ol {
                color: #ffffff;
            }

            button .ant-typography {
                color: #000000;
            }
        `}
`;

export const StyledTextImageDetail = styled.div`
    padding: 50px 100px;
    margin: auto;

    .block-button {
        margin-top: 24px;
    }

    @media screen and (max-width: ${breakpoints.lg}) {
        padding: 50px 40px;
    }

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 24px;
    }
`;

export const StyledTextImageTitle = styled(Title)`
    font-size: 24px;
`;

export const StyledTextImageText = styled.span`
    font-size: 16px;

    & div.ant-typography,
    & .ant-typography p {
        margin-bottom: 24px;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

export const WebPageImageContainer = styled.div`
    position: relative;
    max-width: var(--mobility-page-max-width);
    margin-left: auto;
    margin-right: auto;

    & .ant-image {
        width: 100%;
    }
`;

export const BannerTextContainer = styled.div`
    position: absolute;
    top: 0px;
    left: 0px;
    bottom: 0px;
    right: 0px;
    padding: 0 max(1rem, 7vw);
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
`;

export const TitleContainer = styled.div`
    padding: max(2rem, min(calc(1.46rem + 2.31vw), 4rem)) 0;
    box-sizing: border-box;
`;

const TitleCss = css`
    font-size: 1.5rem;
    line-height: 1.5;
    font-weight: 600;

    @media screen and (min-width: 760px) and (max-width: 999px) {
        font-size: 1.875rem;
        line-height: 1.3333333333;
    }

    @media screen and (min-width: 1000px) and (max-width: 1299px) {
        font-size: 2.25rem;
        line-height: 1.3333333333;
    }

    @media screen and (min-width: 1300px) and (max-width: 1759px) {
        font-size: 2.625rem;
        line-height: 1.2380952381;
    }

    @media screen and (min-width: 1760px) {
        font-size: 3rem;
        line-height: 1.25;
    }
`;

const SubTitleCss = css`
    font-size: 1.5rem;
    line-height: 1;
    font-weight: 400;

    @media screen and (max-width: 999px) {
        font-size: 1rem;
        line-height: 1;
    }
`;

export const WebpageTitle = styled.h1`
    width: 100%;
    color: #fff;
    display: flex;
    white-space: pre-wrap;

    ${TitleCss}
`;

export const WebpageSubTitle = styled.div`
    width: 100%;
    color: #fff;

    ${SubTitleCss}
`;

export const WebPagePageTitle = styled.h2`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    white-space: pre-wrap;
    font-size: 40px;
    line-height: 1;
    font-weight: bold;
    padding: 1.5rem max(1rem, 7vw);
    max-width: var(--mobility-page-max-width);
    margin: auto;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-top: 3rem;
        padding-bottom: 3rem;
    }

    @media screen and (max-width: ${breakpoints.md}) {
        font-size: 24px;
        padding-top: 2.5rem;
        padding-bottom: 2.5rem;
    }
`;

export const StyledColumnBlockCardContainer = styled.div`
    gap: 12px;
    display: flex;
    overflow: auto;

    &::-webkit-scrollbar {
        width: 0;
        background: transparent;
    }
    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 24px 0;
    }
`;

export const StyledColumnBlockContainer = styled(Row)`
    justify-content: center;
`;

export const StyledColumnTitle = styled.h3`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    white-space: pre-wrap;
    font-size: 24px !important;
    line-height: 1 !important;
    font-weight: bold !important;
`;

export const StyledTextImageBlockImageContainer = styled.div``;

export const StyledTextImage = styled(Image)`
    object-fit: cover;
    width: 100%;
    height: 100%;
`;

export const WebpageLayout = styled.div`
    & .ant-pro-page-container-warp {
        width: 100%;
        z-index: 100;

        & .ant-page-header {
            display: none;
        }
    }

    & .ant-pro-page-container-children-content {
        margin: 0;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        & .ant-pro-page-container-warp {
            position: fixed;
        }
    }
`;
export const colImageSpan = { lg: 14, md: 12, sm: 24, xs: 24 };

export const colDetailSpan = { lg: 10, md: 12, sm: 24, xs: 24 };

export const ImageListRow = styled.div`
    height: 100%;
    margin-bottom: 24px;
    display: flex;
    flex-direction: row;
`;

export const ImageListImageContainer = styled.div`
    margin-right: 20px;

    & .ant-image {
        width: 32px;
        height: 32px;

        img {
            object-fit: contain;
        }
    }
`;
export const WebpageCompanyContainer = styled.div`
    padding: 50px;
    max-width: var(--mobility-page-max-width);
    margin: auto;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 24px;
    }
`;

export const CompanyContactUs = styled(Col)``;

export const CompanyConnectContainer = styled(Col)`
    display: flex;
    justify-content: end;

    @media screen and (max-width: ${breakpoints.md}) {
        justify-content: start;
        margin-top: 30px;
    }

    .connect-company {
        color: #959899;
    }
`;

export const CompanyConnextText = styled(Title)`
    color: #959899;
`;

type GetBlockButtonProps = {
    button: WebPageButtonDataFragment;
};

export const StyledLocationContainer = styled.div`
    background-color: #000000;
    height: 100%;
    padding: 50px 50px 0;
    margin-bottom: -24px;
    max-width: var(--mobility-page-max-width);
    margin-left: auto;
    margin-right: auto;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 24px;
    }

    & .ant-select-selector {
        min-width: 250px;
    }
`;

export const LocationTitle = styled(Title)`
    &.ant-typography {
        color: #ffffff;

        @media screen and (max-width: ${breakpoints.md}) {
            font-size: 18px;
            margin-bottom: 16px;
        }
    }
`;

export const StyledBlockButton = styled(Button)`
    display: flex;
    text-align: left;
    height: 48px;
    align-items: center;

    @media screen and (max-width: ${breakpoints.md}) {
        width: 100%;
    }
`;

export const StyledColumnBlockMainContainer = styled.div`
    padding: 30px 0px;
    background-color: #f2f2f2;
    margin: auto;

    &::-webkit-scrollbar {
        width: 0;
        background: transparent;
    }
`;

export const StyledTextCarouselBlockMainContainer = styled.div`
    padding: 54px 0px;
    background-color: white;
    margin: auto;

    &::-webkit-scrollbar {
        width: 0;
        background: transparent;
    }
`;

export const TextImageAssetContainer = styled(Col)`
    & .ant-image {
        width: 100%;
        height: 100%;
    }
`;

export const TextImageDetailsContainer = styled(Col)``;

export const StyledTextImageContainer = styled(Row)<{ isImagePositionLeft: boolean }>`
    ${props =>
        !props.isImagePositionLeft &&
        css`
            flex-flow: row-reverse;
        `}

    @media screen and (max-width: 767px) {
        flex-flow: column;
    }

    max-width: var(--mobility-page-max-width);
    margin: auto;
`;

export const StyledCol = styled(Col)`
    margin-bottom: 10px;
`;

export const BlockButton = ({ button }: GetBlockButtonProps) => {
    const translated = useTranslatedString();
    const { Button: ThemedButton } = useThemeComponents();

    const onClick = useCallback(() => {
        window.open(`https://${button.url}`, '_self');
    }, [button.url]);

    return (
        !isNil(button) &&
        button.isActive && (
            <ThemedButton
                className="block-button"
                itemProp="potentialAction"
                onClick={onClick}
                size="large"
                type="primary"
            >
                <link href={`https://${button.url}`} itemProp="url" />
                <span itemProp="name">{translated(button.text)}</span>
            </ThemedButton>
        )
    );
};

export const StyledMeta = styled(Meta)`
    .ant-card-meta-title {
        color: #000000;
        font-size: 22px;
        font-weight: 900;

        @media screen and (max-width: ${breakpoints.md}) {
            font-size: 16px;
        }
    }

    h4 {
        color: #000000;
        font-size: 22px;
        font-weight: 900;

        @media screen and (max-width: ${breakpoints.md}) {
            font-size: 16px;
        }
    }
    .ant-card-meta-description {
        color: #000000;
        font-size: 16px;
    }
`;

export const ImageTitleBlock = styled(Title)`
    font-size: 24px;
    .ant-typography-h1 {
        font-size: 24px;
    }
`;

export const ImageListTitle = styled(Title)`
    &.ant-typography {
        font-size: 16px;
        margin-bottom: 0;
        line-height: 24px;
    }
`;

export const ImageListDescription = styled(Text)`
    &.ant-typography {
        font-size: 16px;
        margin-bottom: 0;
        line-height: 24px;
    }
`;

export default StyledCard;
